import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import apiService from '../../../services/apiService';
import {
    BRIEF_SECTIONS, SERVICE_STEP_DURATIONS, SERVICE_THINKING_STEPS, fmtPartPrice, fmtServiceDate, fmtServiceDateShort,
    BRIEF_ERROR, parseBrief, serviceUnitMeta, sortWorkOrders, workOrderOneLiner,
} from '../../../utils/serviceHistory';
import './FastTrackAgentTab.css';
import './IronSightCards.css';

// Helpers shared with the full unit page (utils/serviceHistory.js).
const fmtDate = fmtServiceDate;
const fmtDateShort = fmtServiceDateShort;
const fmtPrice = fmtPartPrice;
const getOneLiner = workOrderOneLiner;

// ── Sub-components ────────────────────────────────────────────────────────────

function ListItem({ wo, selected, onClick }) {
    const oneLiner = getOneLiner(wo);
    const hasParts = wo.parts && wo.parts.length > 0;

    return (
        <button
            className={`svc2-list-item ${selected ? 'svc2-list-item--active' : ''}`}
            onClick={onClick}
        >
            <div className="svc2-list-date">{fmtDateShort(wo.closed_date)}</div>
            <div className="svc2-list-title">{oneLiner}</div>
            <div className="svc2-list-meta">
                {hasParts
                    ? <span className="svc2-parts-pill">{wo.parts.length} part{wo.parts.length !== 1 ? 's' : ''}</span>
                    : <span className="svc2-no-parts">no parts</span>
                }
                {wo.parts_source === 'InSalPar' && (
                    <span className="svc2-source-pill">planned</span>
                )}
            </div>
        </button>
    );
}

function DetailPanel({ wo }) {
    if (!wo) {
        return (
            <div className="svc2-detail-empty">
                Select a work order to view details.
            </div>
        );
    }

    const hasCCC = wo.ccc && wo.ccc.length > 0;
    const hasParts = wo.parts && wo.parts.length > 0;

    return (
        <div className="svc2-detail-scroll">
            {/* Detail header */}
            <div className="svc2-detail-header">
                <span className="svc2-detail-date">{fmtDate(wo.closed_date)}</span>
                <span className="svc2-detail-ro">RO #{wo.ro_number}</span>
                {wo.ro_closed_ind === 'Y' && (
                    <span className="svc2-status-closed">Closed</span>
                )}
            </div>

            {/* CCC job lines */}
            {hasCCC && (
                <div className="svc2-ccc-block">
                    {wo.ccc.map((item, i) => (
                        <div key={`${item.job_code}-${i}`} className="svc-job-row">
                            <div className="svc-job-description">{item.job_code || '—'}</div>
                            {item.complaint && (
                                <div className="svc-ccc-row">
                                    <span className="svc-ccc-label svc-ccc-complaint" title="Complaint">C</span>
                                    <span className="svc-complaint">{item.complaint.trim()}</span>
                                </div>
                            )}
                            {item.cause && (
                                <div className="svc-ccc-row">
                                    <span className="svc-ccc-label svc-ccc-cause" title="Cause">C</span>
                                    <span className="svc-complaint">{item.cause.trim()}</span>
                                </div>
                            )}
                            {item.correction && (
                                <div className="svc-ccc-row">
                                    <span className="svc-ccc-label svc-ccc-correction" title="Correction">C</span>
                                    <span className="svc-complaint">{item.correction.trim()}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Parts */}
            {hasParts && (
                <div className="svc2-parts-block">
                    <div className="svc2-parts-heading">
                        <span className="svc-parts-badge">P</span>
                        Parts Used
                        <span className="svc-parts-count">{wo.parts.length}</span>
                    </div>
                    <div className="svc2-parts-table">
                        {wo.parts.map((p, i) => (
                            <div key={`${p.part_no}-${i}`} className="svc2-part-row">
                                <div className="svc2-part-left">
                                    <span className="svc2-part-desc">{p.part_desc || p.part_no}</span>
                                    <span className="svc2-part-no">{p.part_no}</span>
                                </div>
                                {fmtPrice(p.list_price) && (
                                    <span className="svc2-part-price">{fmtPrice(p.list_price)}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!hasCCC && !hasParts && (
                <p className="svc2-empty-wo">No job lines or parts recorded for this work order.</p>
            )}
        </div>
    );
}

// ── AI Summary renderer ───────────────────────────────────────────────────────

const SECTION_LABELS = BRIEF_SECTIONS;

function AISummaryBody({ summary }) {
    const sections = parseBrief(summary);
    return (
        <div className="svc2-ai-summary">
            {sections.map(({ heading, content, lines, bullets: isBullets }) => {
                const meta = heading ? SECTION_LABELS[heading] : null;

                return (
                    <div key={heading || 'raw'} className={`svc2-section ${meta?.cls || ''}`}>
                        {heading && (
                            <div className="svc2-section-label">{meta?.label || heading}</div>
                        )}
                        {isBullets ? (
                            <ul className="svc2-section-bullets">
                                {lines.map((l, i) => (
                                    <li key={i}>{l}</li>
                                ))}
                            </ul>
                        ) : (
                            <p className="svc2-section-text">{content}</p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ── AI Summary panel ──────────────────────────────────────────────────────────

function ChatThread({ stockNumber, summary, unitMeta, serviceHistory }) {
    const [messages, setMessages] = useState([]); // {role, text}[]
    const [input, setInput] = useState('');
    const [asking, setAsking] = useState(false);
    const bottomRef = useRef(null);

    const handleAsk = async () => {
        const q = input.trim();
        if (!q || asking) return;
        const next = [...messages, { role: 'user', text: q }];
        setMessages(next);
        setInput('');
        setAsking(true);
        try {
            const data = await apiService.askServiceHistory(stockNumber, {
                question: q,
                summary,
                history: messages,
                unitMeta: unitMeta || {},
                serviceHistory: serviceHistory || null,
            });
            setMessages([...next, { role: 'assistant', text: data.answer }]);
        } catch {
            setMessages([...next, { role: 'assistant', text: 'Could not get an answer. Please try again.' }]);
        } finally {
            setAsking(false);
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        }
    };

    const handleKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAsk(); }
    };

    return (
        <div className="svc2-chat">
            <div className="svc2-chat-label">Ask a follow-up</div>
            {messages.length > 0 && (
                <div className="svc2-chat-thread">
                    {messages.map((m, i) => (
                        <div key={i} className={`svc2-chat-msg svc2-chat-msg--${m.role}`}>
                            {m.text}
                        </div>
                    ))}
                    {asking && (
                        <div className="svc2-chat-msg svc2-chat-msg--assistant svc2-chat-msg--typing">
                            <span className="svc2-ai-spinner" />
                        </div>
                    )}
                    <div ref={bottomRef} />
                </div>
            )}
            <div className="svc2-chat-input-row">
                <textarea
                    className="svc2-chat-input"
                    rows={2}
                    placeholder='e.g. "Customer is pushing back on price — what should I say?"'
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKey}
                    disabled={asking}
                />
                <button
                    className="svc2-chat-send"
                    onClick={handleAsk}
                    disabled={asking || !input.trim()}
                >
                    Ask
                </button>
            </div>
        </div>
    );
}

function AISummaryPanel({ stockNumber, unitMeta, serviceHistory }) {
    const [state, setState] = useState('idle'); // idle | loading | done | error
    const [summary, setSummary] = useState('');
    const [error, setError] = useState('');

    const handleGenerate = async () => {
        setState('loading');
        setSummary('');
        setError('');
        try {
            const data = await apiService.getServiceHistorySummary(stockNumber, unitMeta || {});
            setSummary(data.summary);
            setState('done');
        } catch (err) {
            setError(BRIEF_ERROR);
            setState('error');
        }
    };

    return (
        <div className="svc2-ai-panel is-card">
            <div className="svc2-ai-header">
                <div className="svc2-ai-head-text">
                    <span className="is-card-eyebrow">AI Service Intelligence</span>
                    <span className="svc2-ai-title is-card-title">Pre-Call Brief</span>
                </div>
                {state !== 'loading' && (
                    <button
                        className="svc2-ai-btn"
                        onClick={handleGenerate}
                        disabled={state === 'loading'}
                    >
                        {state === 'done' ? 'Regenerate' : 'Generate brief'}
                    </button>
                )}
            </div>

            <div className="svc2-ai-body">
                {state === 'idle' && (
                    <p className="svc2-ai-hint">
                        Generate an AI pre-call brief — condition, talking points, and what to watch out for.
                    </p>
                )}

                {state === 'loading' && (
                    <div className="svc2-ai-loading">
                        <span className="svc2-ai-spinner" />
                        Preparing brief for {stockNumber}...
                    </div>
                )}

                {state === 'done' && (
                    <>
                        <AISummaryBody summary={summary} />
                        <ChatThread
                            stockNumber={stockNumber}
                            summary={summary}
                            unitMeta={unitMeta}
                            serviceHistory={serviceHistory}
                        />
                    </>
                )}

                {state === 'error' && (
                    <div className="svc2-ai-error" role="alert">
                        {error}{' '}
                        <button type="button" className="svc2-ai-btn" onClick={handleGenerate}>Retry</button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Thinking loader ───────────────────────────────────────────────────────────

const SVC_THINKING_STEPS = SERVICE_THINKING_STEPS;
const SVC_STEP_DURATIONS = SERVICE_STEP_DURATIONS;

function ServiceThinking({ unit }) {
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (step >= SVC_THINKING_STEPS.length - 1) return;
        const t = setTimeout(() => setStep(s => s + 1), SVC_STEP_DURATIONS[step]);
        return () => clearTimeout(t);
    }, [step]);

    const unitLabel = [unit?.YEAR_MANUF, unit?.MAKE, unit?.MODEL].filter(Boolean).join(' ');

    return (
        <div className="tab-content">
            <div className="fta-thinking-card">
                <div className="fta-thinking-header">
                    <div className="fta-thinking-pulse" />
                    <div>
                        <div className="fta-thinking-title">Loading service history</div>
                        {unitLabel && <div className="fta-thinking-unit">{unitLabel}</div>}
                    </div>
                </div>
                <div className="fta-thinking-steps">
                    {SVC_THINKING_STEPS.map((s, i) => {
                        const state = i < step ? 'done' : i === step ? 'active' : 'pending';
                        return (
                            <div key={i} className={`fta-thinking-step fta-thinking-step--${state}`}>
                                <div className="fta-thinking-step-icon">
                                    {state === 'done'
                                        ? <span className="fta-thinking-check">&#10003;</span>
                                        : state === 'active'
                                            ? <span className="fta-thinking-spinner" />
                                            : <span className="fta-thinking-dot" />}
                                </div>
                                <div className="fta-thinking-step-body">
                                    <span className="fta-thinking-step-label">{s.label}</span>
                                    {state !== 'pending' && (
                                        <span className="fta-thinking-step-detail">{s.detail}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

function ServiceHistoryTab({ serviceHistory, loading, stockNumber, unit, mfOptions, onRegenerate }) {
    const [selectedIndex, setSelectedIndex] = useState(0);

    if (loading) {
        return <ServiceThinking unit={unit} />;
    }

    if (!serviceHistory || serviceHistory.total_ros === 0) {
        return (
            <div className="tab-content">
                <p className="pipeline-empty" style={{ marginTop: '2rem' }}>
                    No workshop service records found for this unit.
                </p>
            </div>
        );
    }

    // Sort newest first
    const sorted = sortWorkOrders(serviceHistory.work_orders);

    const selected = sorted[selectedIndex] || sorted[0];

    return (
        <div className="svc2-root">
            {/* Left — work order list */}
            <div className="svc2-list">
                <div className="svc2-list-header">
                    <div>
                        {serviceHistory.total_ros} work order{serviceHistory.total_ros !== 1 ? 's' : ''}
                        {serviceHistory.total_parts > 0 && (
                            <> &middot; {serviceHistory.total_parts} parts</>
                        )}
                    </div>
                    {onRegenerate && (
                        <button className="svc2-refresh-btn" onClick={onRegenerate} title="Refresh service history">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                            </svg>
                        </button>
                    )}
                </div>
                {serviceHistory.cached_at && (
                    <div className="svc2-cached-at">
                        Loaded {fmtDate(serviceHistory.cached_at)}
                    </div>
                )}
                <div className="svc2-list-scroll">
                    {sorted.map((wo, i) => (
                        <ListItem
                            key={wo.ro_number}
                            wo={wo}
                            selected={i === selectedIndex}
                            onClick={() => setSelectedIndex(i)}
                        />
                    ))}
                </div>
            </div>

            {/* Right — detail panel + AI summary */}
            <div className="svc2-detail">
                {stockNumber && (
                    <AISummaryPanel
                        stockNumber={stockNumber}
                        serviceHistory={serviceHistory}
                        unitMeta={serviceUnitMeta(unit, mfOptions)}
                    />
                )}
                <DetailPanel wo={selected} />
            </div>
        </div>
    );
}

ServiceHistoryTab.propTypes = {
    serviceHistory: PropTypes.shape({
        work_orders: PropTypes.array,
        total_ros: PropTypes.number,
        total_parts: PropTypes.number,
    }),
    loading: PropTypes.bool,
    stockNumber: PropTypes.string,
    unit: PropTypes.object,
    onRegenerate: PropTypes.func,
};

ServiceHistoryTab.defaultProps = {
    serviceHistory: null,
    loading: false,
    stockNumber: null,
    unit: null,
};

export default ServiceHistoryTab;
